import { Button } from '@getro/rombo';
import React, { useEffect } from 'react';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import merge from 'lodash.merge';

import IconRefreshing from '../../../../assets/icon-refreshing.svg';
import { trackEvent } from '../../../../helpers/analytics';
import { useShareContactsMutation } from '../../../../services/userV2';

export const ProfileShareYourContacts = ({ sharedList, user, setIsUserOnboarded }) => {
  const networks = merge([...user?.allyingCollections], [...user?.managingCollections]);
  const currentNetwork = networks.find((network) => network.id === sharedList.collection?.id);
  const [shareContacts, { isLoading }] = useShareContactsMutation();
  const history = useHistory();
  const items = [
    {
      title: `${sharedList.collection?.name} can browse your contacts`,
      description: 'You can always change who can see your contacts.',
    },
    {
      title: `${sharedList.collection?.name} ask you for intros`,
      description: `The ${sharedList.collection?.name} team will ask you for a warm intro when they see a good fit.`,
    },
    {
      title: 'You make the intro',
      description: `You choose which intros to make, helping both ${sharedList?.pointOfContact?.fullName} and your contacts.`,
    },
  ];

  useEffect(() => {
    if (currentNetwork?.shareContacts) {
      setIsUserOnboarded(true);
      history.push('.');
    }
  }, [currentNetwork, history, setIsUserOnboarded]);

  const onClickCancel = () => {
    setIsUserOnboarded(true);
    history.push('.');
  };

  const onClickShare = () => {
    shareContacts({
      collectionId: sharedList.collection?.id,
      share: true,
    })
      .unwrap()
      .then(() => {
        setIsUserOnboarded(true);
        history.push('.');
        trackEvent('contacts:contacts_sharing:enabled', {
          collectionId: sharedList.collection?.id,
          page: 'shared_list_view',
        });
      });
  };

  return (
    <Flex bg="neutral.0" sx={{ gap: '24px', boxShadow: 'medium', borderRadius: 'default', overflow: 'hidden' }}>
      <Box p="24px" width="600px">
        <Box maxWidth="440px" mx="auto">
          <Flex flexDirection="column" alignItems="center">
            <Image mt="8px" src={IconRefreshing} alt="LinkedIn Sync" />
            <Text mt="8px" fontSize="18px" fontWeight="semibold" color="text.dark">
              We’re processing your contacts...
            </Text>
          </Flex>
          <Box mt="56px">
            <Text fontSize="18px" fontWeight="semibold" color="text.dark">
              {`Would you like to share them with ${sharedList?.pointOfContact?.fullName}?`}
            </Text>
            <Text mt="24px" fontSize="14px">
              {`Make it easy to help ${sharedList?.pointOfContact?.fullName}`}:
            </Text>
          </Box>
          <Flex as="ul" pl="0" mt="16px" flexDirection="column" sx={{ gap: '16px', listStyle: 'none' }}>
            {items.map(({ title, description }, index) => (
              <Flex key={title} as="li" alignItems="flex-start" sx={{ gap: '8px' }}>
                <Flex
                  flex="0 0 auto"
                  alignItems="center"
                  justifyContent="center"
                  bg="purple.100"
                  width="24px"
                  height="24px"
                  sx={{ borderRadius: 'circle' }}
                >
                  <Text fontSize="14px" fontWeight="medium" color="purple.400">
                    {index + 1}
                  </Text>
                </Flex>
                <Box flexGrow="1">
                  <Text fontSize="14px" fontWeight="medium" color="text.dark">
                    {title}
                  </Text>
                  <Text mt="4px" fontSize="14px">
                    {description}
                  </Text>
                </Box>
              </Flex>
            ))}
          </Flex>
          <Flex mt="24px" mb="8px" sx={{ gap: '16px' }}>
            <Button
              variant="secondary"
              iconGap="8px"
              onClick={onClickCancel}
              iconPosition="right"
              width="100%"
              justifyContent="center"
            >
              Not for now
            </Button>
            <Button
              iconGap="8px"
              onClick={onClickShare}
              iconPosition="right"
              width="100%"
              justifyContent="center"
              loading={isLoading}
            >
              Yes, share my contacts
            </Button>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
};

ProfileShareYourContacts.propTypes = {
  sharedList: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  setIsUserOnboarded: PropTypes.func.isRequired,
};
